import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Who a PO goes to by default, in plain words (vendorRule.ts on the server):
 * Reece for everything, Test Gauge for Kennedy parts only.
 *
 * The server says which rule vendor a PO's vendor is (`vendor_role`) and
 * which lines are Kennedy items (`is_kennedy`). This file only shows it.
 */
export type VendorRole = "main" | "kennedy";

export const VENDOR_ROLE_TEXT: Record<VendorRole, string> = {
    main: "Reece (main supplier)",
    kennedy: "Test Gauge (Kennedy only)",
};

/** Short tag for the vendor picker: "main supplier" / "Kennedy only". */
export const VENDOR_ROLE_TAG: Record<VendorRole, string> = {
    main: "main supplier",
    kennedy: "Kennedy only",
};

export function asVendorRole(v: unknown): VendorRole | null {
    return v === "main" || v === "kennedy" ? v : null;
}

/** "Reece (main supplier)" chip beside the vendor name. */
export function VendorRoleChip({ role }: { role: VendorRole | null }) {
    if (!role) return null;
    return (
        <span
            data-testid="vendor-role-chip"
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                role === "main"
                    ? "bg-sky-500/15 text-sky-800 dark:text-sky-200"
                    : "bg-amber-500/15 text-amber-800 dark:text-amber-200"
            }`}
        >
            {VENDOR_ROLE_TEXT[role]}
        </span>
    );
}

/**
 * The ⓘ behind the vendor card. Tap (iPad) or click to open: three short
 * sentences and a small labelled picture of the card it explains.
 */
export function VendorRuleInfo() {
    return (
        <Popover>
            <PopoverTrigger
                aria-label="Why this vendor?"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-sm text-foreground/70 hover:bg-accent/60 hover:text-foreground"
            >
                ⓘ
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-2 p-3 text-xs leading-snug">
                <div className="text-sm font-semibold">Why this vendor?</div>
                <p>
                    <strong>Reece is our main supplier.</strong> New orders go to Reece.
                </p>
                <p>
                    <strong>Kennedy is the one exception.</strong> Only Test Gauge sells us Kennedy valves and Kennedy
                    parts, so those orders go to Test Gauge.
                </p>
                <p>
                    <strong>Kennedy parts go on their own Test Gauge order.</strong> If a job needs Kennedy parts and
                    other parts, you get two POs: one to Reece, one to Test Gauge.
                </p>
                <p>
                    Wrong vendor? Press <strong>Change vendor</strong> before you send. A PO that was already sent keeps
                    its vendor.
                </p>
                {/* Labelled picture of the card above, so the words point at something. */}
                <div className="rounded-md border bg-muted/40 p-2" aria-hidden>
                    <div className="flex items-center gap-1.5">
                        <span className="font-semibold">Reece Plumbing Supply</span>
                        <span className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 dark:text-sky-200">
                            Reece (main supplier)
                        </span>
                    </div>
                    <div className="mt-1 text-[10px] text-foreground/70">① who the order goes to</div>
                    <div className="mt-2 flex items-center gap-1.5">
                        <span className="rounded border px-1.5 py-0.5 text-[10px]">6&quot; Kennedy OS&amp;Y valve</span>
                        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200">
                            Test Gauge only
                        </span>
                    </div>
                    <div className="mt-1 text-[10px] text-foreground/70">
                        ② a Kennedy part on a Reece order — move it to a Test Gauge PO
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

/**
 * On a line: this is a Kennedy part, and the PO is going to someone other
 * than Test Gauge. Nothing shows when the line and the vendor agree.
 */
export function KennedyLineFlag({ isKennedy, poRole }: { isKennedy: boolean; poRole: VendorRole | null }) {
    if (!isKennedy || poRole === "kennedy") return null;
    return (
        <span
            data-testid="kennedy-line-flag"
            className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:text-amber-200"
        >
            Test Gauge only — Kennedy part
        </span>
    );
}

export interface SplitPartner {
    id: string;
    po_number: string | null;
    vendor_name: string | null;
    status: string;
}

/**
 * Under the vendor card when the job's parts were split across two POs:
 * "Kennedy parts go on their own Test Gauge order." plus the other PO, and an
 * ⓘ with a labelled picture of the two orders.
 */
export function KennedySplitNote({ partner, onOpen }: { partner: SplitPartner | null; onOpen?: (id: string) => void }) {
    if (!partner) return null;
    const label = partner.po_number ?? "the other PO";
    return (
        <div
            data-testid="kennedy-split-note"
            className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-xs"
        >
            <span className="font-semibold">Kennedy parts go on their own Test Gauge order.</span>
            <span className="text-foreground/75">
                This job&apos;s other order:{" "}
                {onOpen ? (
                    <button type="button" className="font-semibold underline" onClick={() => onOpen(partner.id)}>
                        {label}
                    </button>
                ) : (
                    <span className="font-semibold">{label}</span>
                )}
                {partner.vendor_name ? ` · ${partner.vendor_name}` : ""}
            </span>
            <KennedySplitInfo />
        </div>
    );
}

/** The ⓘ for the split: two short sentences and a labelled picture of the two orders. */
export function KennedySplitInfo() {
    return (
        <Popover>
            <PopoverTrigger
                aria-label="Why two orders?"
                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-sm text-foreground/70 hover:bg-accent/60 hover:text-foreground"
            >
                ⓘ
            </PopoverTrigger>
            <PopoverContent className="w-80 space-y-2 p-3 text-xs leading-snug">
                <div className="text-sm font-semibold">Why two orders?</div>
                <p>
                    Only Test Gauge sells us Kennedy parts, so they get their own order. Everything else stays on the
                    Reece order. The system splits them for you.
                </p>
                <p>
                    When the quote changes, each order updates on its own. An order that was already sent is never
                    changed here.
                </p>
                <div className="space-y-1.5 rounded-md border bg-muted/40 p-2" aria-hidden>
                    <div className="rounded border bg-card p-1.5">
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold">① PO -001</span>
                            <span className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-800 dark:text-sky-200">
                                Reece (main supplier)
                            </span>
                        </div>
                        <div className="text-[10px] text-foreground/70">1&quot; Watts 009 total repair kit</div>
                    </div>
                    <div className="rounded border bg-card p-1.5">
                        <div className="flex items-center gap-1.5">
                            <span className="font-semibold">② PO -002</span>
                            <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200">
                                Test Gauge (Kennedy only)
                            </span>
                        </div>
                        <div className="text-[10px] text-foreground/70">6&quot; Kennedy OS&amp;Y valve</div>
                    </div>
                    <div className="text-[10px] text-foreground/70">① everything else · ② the Kennedy parts</div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
